import { invokeProtectedFunction } from "@/lib/invokeProtectedFunction";
import type { PlanTier } from "@/lib/authz/plans";
import type { Money } from "@/lib/billing/money";

/**
 * Checkout-based upgrade: the server validates the existing subscription,
 * records the pending change, and returns a Shopify checkout URL for the NEW
 * tier. Payment happens in checkout (synchronous, card actually charged); the
 * old subscription is cancelled automatically in the background once the new
 * one is live.
 */
export type ChangePlanSuccess = {
  ok: true;
  checkout: true;
  /** Redirect the browser here — Shopify checkout for the new tier. */
  checkoutUrl: string;
  fromTier: PlanTier;
  toTier: PlanTier;
  billing: "monthly" | "annual";
  /** Catalog price of the new tier in the subscription's currency (display). */
  price: Money;
};

/** No-write dry run for the confirm dialog. */
export type ChangePlanQuote = {
  ok: true;
  preview: true;
  checkout: true;
  fromTier: PlanTier;
  toTier: PlanTier;
  billing: "monthly" | "annual";
  price: Money;
  /** True when an unused-time credit will be pre-applied to the checkout. */
  prorated: boolean;
  /** Value of the current plan's unused days (0 when no credit applies). */
  unusedCredit: number;
  /** Expected first checkout payment (full price minus the credit); Shopify shows the exact figure. */
  estimatedFirstPayment: number;
  currency: string;
};

export type ChangePlanFailure = {
  ok: false;
  /** ApiError code from the server ("legacy_subscription", "unsupported_currency", …). */
  code: string;
};

export type ChangePlanResult = ChangePlanSuccess | ChangePlanFailure;

function failureCode(error: unknown): string {
  const payload = (error as { payload?: { code?: unknown } }).payload;
  return typeof payload?.code === "string" ? payload.code : "request_failed";
}

/** Dry-run quote: validates feasibility and returns tiers/interval/price. */
export async function previewSubscriptionPlanChange(
  tier: PlanTier,
): Promise<ChangePlanQuote | ChangePlanFailure> {
  try {
    const { data, error } = await invokeProtectedFunction<ChangePlanQuote>(
      "change-subscription-plan",
      { body: { tier, preview: true } },
    );
    if (error) return { ok: false, code: failureCode(error) };
    if (!data?.ok || data.preview !== true) return { ok: false, code: "request_failed" };
    return data;
  } catch (err) {
    console.error("[change-plan] preview failed:", err);
    return { ok: false, code: "request_failed" };
  }
}

// One plan change at a time per browser context (double-dispatch would churn
// the pending intent and mint extra checkout URLs).
let changeInFlight = false;

/**
 * Start the upgrade: returns the checkout URL to redirect to. No money moves
 * here — the charge happens in Shopify checkout, so a failed/ambiguous call is
 * always safe to retry.
 */
export async function changeSubscriptionPlan(tier: PlanTier): Promise<ChangePlanResult> {
  if (changeInFlight) {
    return { ok: false, code: "plan_change_in_progress" };
  }
  changeInFlight = true;
  try {
    const { data, error } = await invokeProtectedFunction<ChangePlanSuccess>(
      "change-subscription-plan",
      { body: { tier } },
    );
    if (error) {
      // The invoke facade surfaces ApiClientError with the parsed jsonError
      // body ({error: message, code}) on `.payload`.
      return { ok: false, code: failureCode(error) };
    }
    if (!data?.ok || typeof data.checkoutUrl !== "string" || !data.checkoutUrl) {
      return { ok: false, code: "request_failed" };
    }
    return data;
  } catch (err) {
    console.error("[change-plan] failed:", err);
    return { ok: false, code: "request_failed" };
  } finally {
    changeInFlight = false;
  }
}
